import { isRangedWeapon, weaponHasProperty, weaponRangeFeet, weaponReachFeet } from '../../../shared/rules/weaponRange.mjs';

/**
 * How a hovered target reads while choosing it for an attack or spell. Grey: out of range, or behind Total Cover (it
 * can't be targeted). Red: the roll has Disadvantage. Yellow: the target has Half or Three-Quarters Cover (harder to
 * hit). Green: a normal roll. Advantage adds a shine to the green, or to the yellow when the target also has cover.
 * Advantage and Disadvantage together cancel to a normal roll before this is asked.
 */
export const TARGET_TONES = Object.freeze({
  normal: Object.freeze({ label: 'Normal roll' }),
  advantage: Object.freeze({ label: 'Advantage', shine: true }),
  cover: Object.freeze({ label: 'Half Cover (+2 AC)' }),
  'cover-advantage': Object.freeze({ label: 'Advantage · Half Cover (+2 AC)', shine: true }),
  disadvantage: Object.freeze({ label: 'Disadvantage' }),
  'out-of-range': Object.freeze({ label: 'Out of range' }),
  'total-cover': Object.freeze({ label: "Total Cover: can't be targeted" }),
  'in-range': Object.freeze({ label: 'In range' }),
});

/** The tone for a target. `rollsToHit` is false for spells without an attack roll: only range matters then. */
export const targetTone = ({ inRange = true, mode = 'normal', cover = null, rollsToHit = true } = {}) => {
  if (!inRange) return 'out-of-range';
  if (cover?.degree === 'total') return 'total-cover';
  if (!rollsToHit) return 'in-range';
  if (mode === 'disadvantage') return 'disadvantage';
  if (cover) return mode === 'advantage' ? 'cover-advantage' : 'cover';
  return mode === 'advantage' ? 'advantage' : 'normal';
};

/** A one-line description of a target's tone, naming why when the rules say why (the cover's degree and source). */
export const describeTarget = ({ tone, sources = [], reason, cover = null } = {}) => {
  if (tone === 'out-of-range') return reason || TARGET_TONES[tone].label;
  const behind = cover?.sources?.length ? ` (${cover.sources[0].toLowerCase()})` : '';
  if (tone === 'total-cover') return `Total Cover${behind}: can't be targeted`;
  const coverText = cover && cover.bonus ? `${cover.label} (+${cover.bonus} AC)${behind}` : null;
  const base = tone === 'cover' && coverText ? coverText
    : tone === 'cover-advantage' && coverText ? `Advantage · ${coverText}` : TARGET_TONES[tone]?.label || '';
  const why = (tone === 'disadvantage' || tone === 'advantage' || tone === 'cover-advantage') && sources.length ? `: ${sources.join(', ')}` : '';
  return `${base}${why}`;
};

/**
 * The square of grid cells within `feet` of a footprint (top-left `anchor`, `squares` wide). Diagonals count 5 feet on
 * this grid, so everything in range forms a square, not a circle.
 */
export const rangeOutline = ({ anchor, squares = 1, feet }) => {
  const reach = Math.max(0, Math.floor(Number(feet) / 5) || 0);
  return { x: anchor.x - reach, y: anchor.y - reach, size: squares + 2 * reach };
};

/**
 * Outlines to draw for a weapon: its reach, and for a ranged or Thrown weapon its normal range and its long range
 * (dashed, where attacks have Disadvantage).
 */
export const weaponRangeRings = ({ weapon, unarmed = false } = {}) => {
  const rings = [];
  const ranged = !unarmed && isRangedWeapon(weapon);
  if (!ranged) rings.push({ feet: unarmed ? 5 : weaponReachFeet(weapon), kind: 'reach' });
  const range = unarmed ? null : weaponRangeFeet(weapon);
  if (range && (ranged || weaponHasProperty(weapon, 'thrown'))) {
    rings.push({ feet: range.normal, kind: 'normal' });
    if (range.long > range.normal) rings.push({ feet: range.long, kind: 'long' });
  }
  return rings;
};

/** The range in feet written on a spell ("120 feet", "Touch", "Self"), or null when it has no distance. */
export const spellRangeFeet = (range) => {
  if (typeof range === 'number') return range > 0 ? range : null;
  const text = String(range || '').trim().toLowerCase();
  if (text === 'touch') return 5;
  const feet = /(\d+)\s*(?:feet|foot|ft)/.exec(text);
  if (feet) return Number(feet[1]);
  const miles = /(\d+)\s*mile/.exec(text);
  return miles ? Number(miles[1]) * 5280 : null;
};
